import i18n from '@/core/i18n';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';
import { OverheadStatus, OverheadType } from '@/modules/finance/overheads/types/overhead';

export class OverheadStatusPending implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-slate-500 text-slate-500',
    };
  }
}

export class OverheadStatusInvoiced implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-emerald-500 text-emerald-500',
    };
  }
}

export class OverheadStatusPlanned implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-amber-500 text-amber-500',
    };
  }
}

// Teal (not emerald) so the Paid dot/badge is distinguishable from Invoiced
// in the status filter, while both still read as "green = settled".
export class OverheadStatusPaid implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-teal-500 text-teal-500',
    };
  }
}

export class OverheadTypeIncomeStatus implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-emerald-500 text-emerald-500',
    };
  }
}

export class OverheadTypeCostStatus implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-orange-500 text-orange-500',
    };
  }
}

export class OverheadStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

/**
 * Resolves badge styling for overhead statuses AND overhead types — both the
 * Status and Type columns render through `StatusBadgeType.Overhead`, so this
 * factory covers all six wire values.
 */
export const createOverheadStatus = (status: string): BaseStatus => {
  switch (status) {
    case OverheadStatus.Pending:
      return new OverheadStatusPending();
    case OverheadStatus.Invoiced:
      return new OverheadStatusInvoiced();
    case OverheadStatus.Planned:
      return new OverheadStatusPlanned();
    case OverheadStatus.Paid:
      return new OverheadStatusPaid();
    case OverheadType.Income:
      return new OverheadTypeIncomeStatus();
    case OverheadType.Cost:
      return new OverheadTypeCostStatus();
    default:
      return new OverheadStatusUnknown();
  }
};

export const getOverheadStatusText = (status: OverheadStatus): string => {
  switch (status) {
    case OverheadStatus.Pending:
      return i18n.t('authenticated.overheads:status.pending');
    case OverheadStatus.Invoiced:
      return i18n.t('authenticated.overheads:status.invoiced');
    case OverheadStatus.Planned:
      return i18n.t('authenticated.overheads:status.planned');
    case OverheadStatus.Paid:
      return i18n.t('authenticated.overheads:status.paid');
    default:
      return i18n.t('common:unknown');
  }
};

export const getOverheadStatusColor = (status: OverheadStatus): string => {
  switch (status) {
    case OverheadStatus.Pending:
      return 'slate-500';
    case OverheadStatus.Invoiced:
      return 'emerald-500';
    case OverheadStatus.Planned:
      return 'amber-500';
    case OverheadStatus.Paid:
      return 'teal-500';
    default:
      return 'muted-foreground';
  }
};

export const getOverheadTypeText = (type: OverheadType): string => {
  switch (type) {
    case OverheadType.Income:
      return i18n.t('authenticated.overheads:type.income');
    case OverheadType.Cost:
      return i18n.t('authenticated.overheads:type.cost');
    default:
      return i18n.t('common:unknown');
  }
};

export const getOverheadTypeColor = (type: OverheadType): string => {
  switch (type) {
    case OverheadType.Income:
      return 'emerald-500';
    case OverheadType.Cost:
      return 'orange-500';
    default:
      return 'muted-foreground';
  }
};

export const getOverheadStatusOptions = createStatusOptionsMapper<OverheadStatus>({
  getStatusColor: getOverheadStatusColor,
  getStatusText: getOverheadStatusText,
});

export const getOverheadTypeOptions = createStatusOptionsMapper<OverheadType>({
  getStatusColor: getOverheadTypeColor,
  getStatusText: getOverheadTypeText,
});
